/**
 * A MUI tooltip for an icon the aiWARE app-bar widget owns (VE-26426).
 *
 * Both header icons are raw HTML handed to the widget's `rightActions`, so they
 * sit outside React's tree and cannot be wrapped in a `<Tooltip>` the ordinary
 * way. They used the native `title` attribute instead, which gives the browser's
 * own slow grey tooltip — different in look and timing from the ~49 files that
 * use MUI's, including everything else in this header's neighbourhood.
 *
 * So the tooltip is anchored rather than wrapped: this renders from the app's
 * React tree, points a `<Tooltip>`'s popper at the injected node, and drives
 * `open` from listeners on that node. Nothing is portalled into the widget's
 * DOM, so the widget can rebuild its markup as often as it likes; the node is
 * re-found when it does.
 *
 * It also puts the icon's accessible name back. `title` was doing two jobs —
 * drawing the native tooltip AND naming the element — so replacing it with a
 * MUI tooltip took the name with it and left the widget's clickable action with
 * nothing but an `aria-hidden` glyph inside it. A MUI tooltip cannot stand in:
 * its `aria-describedby` goes on the hidden anchor below, which is in our tree
 * rather than the widget's.
 */
import { useEffect, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';

import { observeWidgetDom } from './widgetDomWatcher';

interface PropTypes {
  /** id of the element the widget injected. */
  readonly nodeId: string;
  /** Shown as the tooltip, and used as the icon's accessible name. */
  readonly title: string;
}

/**
 * The injected node, re-found whenever the widget replaces it.
 *
 * Through the shared watcher, so the two tooltips and the download icon's own
 * re-assertion cost one document-wide observer between them rather than three.
 */
const useInjectedNode = (nodeId: string): HTMLElement | null => {
  const [node, setNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const find = () =>
      setNode((current) => {
        const found = document.getElementById(nodeId);
        return found === current ? current : found;
      });

    find();
    return observeWidgetDom(find);
  }, [nodeId]);

  return node;
};

const HeaderIconTooltip = ({ nodeId, title }: PropTypes) => {
  const node = useInjectedNode(nodeId);
  const [open, setOpen] = useState(false);

  /**
   * Named for a screen reader, which the visual tooltip does nothing for. Set as
   * an attribute rather than written into the widget's markup, so there is no
   * escaping to hand-roll — the whole reason the markup carries no text.
   *
   * Re-applied whenever the widget swaps the node out, since `node` changes with
   * it, and whenever the locale changes `title`.
   */
  useEffect(() => {
    node?.setAttribute('aria-label', title);
  }, [node, title]);

  useEffect(() => {
    if (!node) {
      return undefined;
    }
    const show = () => setOpen(true);
    const hide = () => setOpen(false);
    node.addEventListener('mouseenter', show);
    node.addEventListener('mouseleave', hide);
    node.addEventListener('focusin', show);
    node.addEventListener('focusout', hide);
    // Clicking the icon opens a panel over it; leaving the tooltip up would put
    // it on top of what the click just revealed.
    node.addEventListener('click', hide);
    return () => {
      node.removeEventListener('mouseenter', show);
      node.removeEventListener('mouseleave', hide);
      node.removeEventListener('focusin', show);
      node.removeEventListener('focusout', hide);
      node.removeEventListener('click', hide);
      setOpen(false);
    };
  }, [node]);

  if (!node) {
    return null;
  }

  return (
    <Tooltip
      open={open}
      title={title}
      disableInteractive
      slotProps={{ popper: { anchorEl: node } }}
    >
      {/* Anchoring happens through PopperProps; this child is only what MUI
          needs to hold a ref, and is never seen. */}
      <span data-testid={`${nodeId}-tooltip-anchor`} />
    </Tooltip>
  );
};

export default HeaderIconTooltip;
